import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, IconButton, Button, TextField, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import { ConstantActions, LeadFormActions } from '../../Services/_actions/index.js';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {  userInformationAtom } from '../../Services/_states/index.js';
import { useParams } from 'react-router';
import DynamicFormFieldsList from './DynamicFormFields/List.tsx';
import {
  convertGroupFormIntoArr,
  groupLeadItems,
  validateFormFieldValue,
} from './DynamicFormFields/helper.ts';
import { validateDynamicForm } from './DynamicFormFields/helper.ts';
import { campaignLeadManagementAtom } from '../../Services/_states/index.js';
import CommentBox from '../Shared/CommentBox/index.tsx';
import { leadsActionSelector } from '../../Services/_states/Leads/leadSelector.ts';

interface FormDetailsModalProps {
  open: boolean;
  onClose: (data, type) => void;
  selectedRow?: any;
  dynamicFormFields?: any[];
  currentCampaignId?: any;
  currentLeadFormId?: any;
  parentLead?: any;
  dynamicFormGroups?: any[];
  multipleFormUpdate?: boolean;
}

const FormDetailsModal: React.FC<FormDetailsModalProps> = ({
  open,
  onClose,
  selectedRow,
  dynamicFormFields = [],
  dynamicFormGroups = [],
  parentLead = null,
  currentCampaignId = null,
  currentLeadFormId = null,
  multipleFormUpdate = false
}) => {
    console.log("currentLeadFormId",currentLeadFormId)

  const actions = LeadFormActions();
  const setLeadActions = useSetRecoilState(leadsActionSelector);
  const constantActions = ConstantActions();
  const { showSnackBar } = useSnackBar();
  const [fieldErrors, setFieldErrors] = useState < { [key: number]: string } > ({});
  const [errors, setErrors] = useState < any > ('');
  const [phoneNumber, setPhoneNumber] = useState < any > (null);
  const [isUploading, setIsUploading] = useState(false);
  const campaignLeadManagementData = useRecoilValue(campaignLeadManagementAtom);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const selectedLeadFormId =
    currentLeadFormId ?? campaignLeadManagementData?.selectedForm?.leads_form_id;
  const statusList = campaignLeadManagementData?.statusData?.[selectedLeadFormId] ?? [];
  const [statusUpdate, setStatusUpdate] = useState({});
  const [dynamicFormData, setDynamicFormData] = useState < any > ({});
  const [commentList, setCommentList] = useState < any[] > ([]);
  const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf', 'mp4'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  const validateFileBeforeUpload = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !ALLOWED_EXTENSIONS.includes(ext)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Max allowed size is ${MAX_FILE_SIZE / (1024 * 1024)} MB`;
    }
    return null;
  };

  useEffect(() => {
    if (!open) {
      setDynamicFormData([]);
      setCommentList([]);
      return;
    }
    try {
      const sourceData = selectedRow?.data ?? parentLead?.data ?? [];
      const fieldMap = new Map < string, any> (
        sourceData.map((item: any) => [item.key_name, item])
      ); let populated = (dynamicFormFields ?? []).map((field: any) => {
        const source = fieldMap.get(field?.key_name);

        return {
          ...field,
          value: source?.value ?? field.value ?? '',
          status_config: source?.status_config ?? field.status_config ?? null,
          entity_data: source?.entity_data ?? field.entity_data ?? null,
        };
      });
      const groupedForm = groupLeadItems(populated, dynamicFormGroups);
      setDynamicFormData(groupedForm);
    } catch (error) {
      showSnackBar(error?.toString() ?? 'Unexpected error', 'error');
    }
    setCommentList(selectedRow?.internal_comments || []);
  }, [open, dynamicFormFields, selectedRow?.data, parentLead?.data, statusList]);

  const addComment = async (comment: string, leadId: string | number) => {
    try {
      const response: any = await actions.postCommentById({ comment, id: leadId }, leadId);
      if (response?.status !== 200) {
        showSnackBar('Failed to add comment', 'error');
        return false;
      }
      return true;
    } catch (error) {
      showSnackBar(error?.toString() ?? 'Server error. Please try again.', 'error');
      return false;
    }
  };

  const updateStatusInLoop = async () => {
    if (!selectedRow?.id) return;

    try {
      const statusPayloads = [
        ...Object.values(statusUpdate?.status ?? {}),
        ...Object.values(statusUpdate?.sub_status ?? {}),
      ]
        ?.filter(
          (item: any) =>
            Boolean(item) && item?.status_id && (item?.status_name || item?.sub_status_name)
        )
        ?.map((item) => ({
          ...item,
          id: selectedRow.id,
        }));

      if (statusPayloads?.length === 0) {
        console.log('No valid status updates to process');
        return;
      }

      const results = await Promise.allSettled(
        statusPayloads.map((payload) => actions.updateLeadStatusDate(payload))
      );

      results.forEach((res, idx) => {
        const status = statusPayloads[idx];
        const label = status.status_name || status.sub_status_name || 'unknown';

        if (res.status === 'fulfilled' && (res.value as any)?.status === 200) {
          console.log(`✅ Status updated for ${label}`);
        } else {
          console.error(`❌ Failed to update ${label}`, res);
        }
      });
    } catch (err) {
      console.error('Unexpected error in updateStatusInLoop:', err);
    }
  };

  const handleFieldChange = (index: any, value: any, groupTitle: string) => {
    const updatedData = { ...dynamicFormData };
    const groupForm = [...updatedData[groupTitle]];
    let updatedField = { ...groupForm[index], value: value };
    if (value && typeof value === 'object' && value?.collection === 'entity') {
      updatedField = { ...updatedField, entity_data: value ?? null, value: value?.value ?? '' };
    } else if (
      updatedField?.statusOption === 'is_status' ||
      updatedField?.statusOption === 'is_sub_status'
    ) {
      if (updatedField?.statusOption === 'is_status') {
        updatedField = {
          ...updatedField,
          status_config: value ?? null,
          value: value?.status_name ?? '',
        };
      } else if (updatedField?.statusOption === 'is_sub_status') {
        updatedField = { ...updatedField, status_config: value ?? null, value: value?.name ?? '' };
      }
      setStatusUpdate((prev) => {
        if (updatedField?.statusOption === 'is_status') {
          return {
            ...prev,
            status: {
              ...prev.status,
              [updatedField.key_name]: {
                status_id: value?.status_id ?? '',
                status_name: value?.status_name ?? '',
                type_of_status: value?.type_of_status ?? '',
                status: value?.status_name ?? '',
              },
            },
          };
        }
        if (updatedField?.statusOption === 'is_sub_status') {
          return {
            ...prev,
            sub_status: {
              ...(prev?.sub_status ?? {}),
              [updatedField.key_name]: {
                sub_status_id: value?.sub_status_id ?? '',
                sub_status_name: value?.name ?? '',
                type_of_status: value?.type_of_status ?? updatedField?.key_name ?? '',
                status_id: value?.status_id ?? '',
                status_name: value?.status_name ?? '',
              },
            },
          };
        }
      });
    }
    groupForm[index] = updatedField;
    updatedData[groupTitle] = [...groupForm];
    setDynamicFormData(updatedData);
    const errorMsg = validateFormFieldValue(updatedField, value);
    if (errorMsg) {
      setFieldErrors((prev) => ({ ...prev, [`${groupTitle}_${index}`]: errorMsg }));
    } else {
      setFieldErrors((prev) => {
        const { [`${groupTitle}_${index}`]: _, ...rest } = prev;
        return { ...rest };
      });
    }
  };

  const uploadFile = async (file: File): Promise<string> => {
    const validationError = validateFileBeforeUpload(file);
    if (validationError) {
      showSnackBar(validationError, 'error');
      return '';
    }
    try {
      setIsUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const resp = await constantActions.uploadFileS3Bucket(formData);
      if (resp?.status === 200 && resp?.data?.data?.url) {
        return resp.data.data.url;
      }
      return '';
    } catch (error) {
      showSnackBar('File upload failed', 'error');
      return '';
    } finally {
      setIsUploading(false);
    }
  };
  const handleSave = async () => {
    try {
      const { fieldErrors, hasErrors } = validateDynamicForm(dynamicFormData);

      if (hasErrors) {
        setFieldErrors(fieldErrors);
        showSnackBar('Please fill all required fields', 'error');
        return;
      } else {
        setFieldErrors({});
      }

      let payload: any = {
        area: selectedRow?.area || '',
        city: selectedRow?.city || '',
        lead_id: selectedRow?.lead_id || '',
        leads_form_entries: [],
        phone_number: selectedRow?.phone_number || '',
        supplier_id: selectedRow?.supplier_id || '',
        supplier_name: selectedRow?.supplier_name || '',
      };

      const newArr: any = (convertGroupFormIntoArr(dynamicFormData) || [])?.filter(
        (item: any) => !Boolean(item?.is_static_column)
      );

      const processedEntries = await Promise.all(
        newArr.map(async (item) => {
          if (
            item &&
            typeof item === 'object' &&
            item.key_type === 'FILE' &&
            item.value instanceof File
          ) {
            const fileUrl = await uploadFile(item.value);
            return { ...item, value: fileUrl };
          }
          return item && typeof item === 'object' ? item : {};
        })
      );

      if (!newArr || newArr.length === 0) {
        showSnackBar('Form cannot be submitted without entries.', 'error');
        return;
      }

      let entryList = processedEntries.map((item) => {
        if (phoneNumber) {
          payload.phone_number = phoneNumber || '';
        } else if (item?.key_type === 'PHONE') {
          payload.phone_number = item.value ?? '';
        }
        return {
          key_name: item?.key_name,
          value: item?.entity_data?.value ? item?.entity_data?.value ?? '' : item?.value ?? '',
          ...(item?.entity_data && { entity_data: item?.entity_data ?? null }),
          ...(item?.status_config && { status_config: item?.status_config ?? null }),
        };
      });

      payload.leads_form_entries = entryList;
      payload.leads_form_id = isNaN(parseInt(currentLeadFormId)) ? '' : parseInt(currentLeadFormId);
      payload.parent_id = { lead_id: parentLead?.lead_id || '' };

      setErrors('');
      if (!payload?.phone_number) {
        setErrors('Phone number is required.');
        return;
      } else if (/[^0-9]/.test(payload?.phone_number)) {
        setErrors('Phone number should only contain digits.');
        return;
      } else if (payload?.phone_number.length < 10) {
        setErrors('Phone number must be at least 10 digits long.');
        return;
      }

      // 🔹 Save (create or update)
      let res: any;
      let leadIdAfterSave: string | number | null = selectedRow?.id ?? null;

      if (!selectedRow?.id) {
        res = await actions.insertLeadV1(payload);
        if (res?.status === 200) {
          // try to pick id from common places
          leadIdAfterSave =
            res?.data?.data?.id ??
            res?.data?.id ??
            res?.data?.data?.lead_id ?? // fallback if API returns lead_id only
            null;
        }
      } else {
        res = await actions.updateLeadV1(payload);
        if (res?.status === 200) {
          updateStatusInLoop();
        }
      }

      if (res?.status === 200) {
        const toPost = commentList.filter((c) => c.isNewComment && c.comment?.trim());
        if (toPost.length > 0 && leadIdAfterSave) {
          const results = await Promise.allSettled(
            toPost.map((c) => addComment(c.comment, leadIdAfterSave as any))
          );
          const anyFailed = results.some(
            (r) => r.status === 'rejected' || (r.status === 'fulfilled' && r.value === false)
          );
          if (anyFailed) {
            showSnackBar('Some comments failed to save', 'warning');
          } else {
            // strip flags so the UI reflects that they are persisted
            setCommentList((prev) => prev.map((c) => ({ ...c, isNewComment: false })));
          }
        }

        if (toPost.length > 0 && leadIdAfterSave) {
          setLeadActions({
            type: "update", payload: {
              ...selectedRow, internal_comments: [
                ...(selectedRow?.internal_comments ?? []),
                ...toPost.map((c) => ({
                  comment: c.comment,
                  created_by: loggedInUser?.username ?? 'You',
                  created_at: new Date().toISOString(),
                })),
              ],
              last_machadalo_comment: {
                comment: toPost[toPost.length - 1].comment,
                created_by: loggedInUser?.username ?? 'You',
                created_at: new Date().toISOString(),
              },
            }
          })
        }

        showSnackBar('Form saved successfully!', 'success');
        onClose(newArr, selectedRow?.id ? 'update' : 'create');
      }
    } catch (error: any) {
      console.error('Unexpected error while saving:', error);
      showSnackBar('An unexpected error occurred. Please check console logs.', 'error');
    }
  };

  useEffect(() => {
    if (selectedRow?.phone_number) {
      setPhoneNumber(selectedRow?.phone_number || '');
    } else if (parentLead?.phone_number) {
      setPhoneNumber(parentLead?.phone_number || '');
    }
  }, [selectedRow]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          // width: 1000,
          width: {
            xs: '90%',
            sm: '80%',
            md: '70%',
            lg: '1000px',
          },
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" fontWeight="bold">
            Form Details
          </Typography>
          <IconButton onClick={() => onClose('', '')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          gap={2}
          sx={{ maxHeight: 300, overflow: 'auto', pr: 1, pt: 1 }}
        >
          {!multipleFormUpdate && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label={
                  <Typography component="span">
                    Phone Number <span style={{ color: 'red' }}>*</span>
                  </Typography>
                }
                placeholder="Enter Mobile Number"
                variant="outlined"
                size="small"
                value={phoneNumber || ''}
                disabled={
                  (selectedRow?.lead_id && selectedRow?.phone_number) ||
                  (parentLead?.lead_id && parentLead?.phone_number) ||
                  false
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  let mobileError = '';
                  if (/[^0-9]/.test(value)) {
                    mobileError = 'Characters are not allowed';
                  }
                  setErrors(mobileError);
                  if (!mobileError) {
                    setPhoneNumber(value);
                    setErrors('');
                  }
                }}
                error={!!errors}
                helperText={errors || ''}
                inputProps={{
                  maxLength: 12,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            </Grid>
          </Grid>
          )}
          <DynamicFormFieldsList
            data={dynamicFormData || {}}
            onChange={handleFieldChange}
            // storeEntityValue={storeEntityValue}
            fieldErrors={fieldErrors}
            phoneNumber={phoneNumber}
            isCreatingLead={!selectedRow?.id}
             multipleFormUpdate={multipleFormUpdate}
          />
        </Box>
        <Box mt={2}>
          <Typography variant="h6" gutterBottom>
            Comments
          </Typography>
          <CommentBox
            commentList={commentList || []}
            onSubmitComment={(text: string) => {
              if (!text?.trim()) return;
              const newComment = {
                comment: text.trim(),
                created_at: new Date(),
                created_by: loggedInUser?.username ?? 'You',
                isNewComment: true, // flag so we know to submit on save
              };
              setCommentList((prev) => [...prev, newComment]);
            }}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" size="small" onClick={() => onClose('', '')} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" size="small" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
export default FormDetailsModal;
